import React from 'react';
import { TVABreakdown } from '../../types/estimation-items';
import { Info } from 'lucide-react';

interface TVABreakdownPanelProps {
  tvaBreakdown: TVABreakdown[];
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  buildingAge?: number;
}

const getTVARateLabel = (rate: number): string => {
  switch (rate) {
    case 5.5:
      return 'Taux réduit (travaux d\'amélioration énergétique)';
    case 10:
      return 'Taux intermédiaire (travaux de rénovation)';
    case 20:
      return 'Taux normal';
    default:
      return `TVA ${rate}%`;
  }
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const TVABreakdownPanel: React.FC<TVABreakdownPanelProps> = ({
  tvaBreakdown,
  totalHT,
  totalTVA,
  totalTTC,
  buildingAge
}) => {
  return (
    <div style={{ background: 'white', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', padding: '18px 20px' }}>
      <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <Info size={13} color="var(--color-text-secondary)" />
        Détail de la TVA applicable
      </h3>

      {buildingAge !== undefined && buildingAge < 2 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: '#FFF7ED', borderRadius: 'var(--radius-sm)', padding: '6px 10px', marginBottom: 10 }}>
          <Info size={11} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: '#92400E' }}>
            Logement &lt; 2 ans : TVA 20% sur l'ensemble des travaux.
          </p>
        </div>
      )}

      {buildingAge !== undefined && buildingAge >= 2 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: '#ECFDF5', borderRadius: 'var(--radius-sm)', padding: '6px 10px', marginBottom: 10 }}>
          <Info size={11} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: '#059669' }}>
            Logement &gt; 2 ans : taux réduits (5,5% et 10%) selon travaux.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tvaBreakdown.map((item) => (
          <div key={item.rate} style={{ border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>TVA {item.rate}%</span>
                <span>Base HT : {formatCurrency(item.baseHT)}</span>
                <span>TVA : {formatCurrency(item.tvaAmount)}</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                TTC : {formatCurrency(item.totalTTC)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: '#FEF3C7', borderRadius: 'var(--radius-sm)', padding: '6px 10px', marginTop: 10 }}>
        <p style={{ fontSize: 11, color: '#92400E' }}>
          <strong>Important :</strong> Taux réduits sous réserve de conditions légales. Attestation requise.
        </p>
      </div>
    </div>
  );
};

export default TVABreakdownPanel;
